import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import StaffingBudgetFormSectionAllAttributes
	from "modules/project/components/forms/formSections/StaffingBudgetFormSectionAllAttributes";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Avatar from "~/components/Avatar";
import Button from "~/components/buttons/Button/Button.tsx";
import ComboBox from "~/components/formElements/ComboBox";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Select from "~/components/formElements/Select";
import SubmitButton from "~/components/formElements/SubmitButton";
import TimingFormSection from "~/components/formSections/TimingFormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import useCareerLevelOptions from "~/hooks/form/formOptionsData/useCareerLevelOptions.ts";
import useProjectsProjectRolesOptions from "~/hooks/form/formOptionsData/useProjectsProjectRolesOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import type { CareerLevelId } from "~/modules/humanResources/api/careerLevel/careerLevelTypes.ts";
import { getCareerLevelDisplayNameById } from "~/modules/humanResources/utils/careerLevelUtils.ts";
import type { Order } from "~/modules/project/api/order/orderTypes.ts";
import { createAdditionalStaffingForUser } from "~/modules/project/api/staffing/staffingApiDispatchers.ts";
import type { CreateStaffingData } from "~/modules/project/api/staffing/staffingTypes.ts";
import UsersOtherStaffingsInOrderFormSection
	from "~/modules/project/components/forms/formSections/UsersOtherStaffingsInOrderFormSection";
import type {
	StaffingsTableData,
} from "~/modules/project/components/ProjectDetailsView/components/OrderDetails/components/StaffingsTable/staffingsTableTypes.ts";
import { findNextAvailableStartDateForStaffing } from "~/modules/project/utils/staffingUtils.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { doDateRangesWithOptionalEndDatesOverlap, formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter, transformEmptyDateValueToNull } from "~/utils/form/formUtils.ts";

type CreateStaffingFormProps = {
	onSuccess: (staffedUserId: string) => void;
	onCancel: () => void;
	orderData: Order;
	staffedUserData: User;
	totalBudgetUnassigned: number;
	totalManDaysUnassigned: number;
	usersStaffings: StaffingsTableData[];
};

interface FormData
	extends Omit<CreateStaffingData, "orderId" | "projectId" | "projectPhaseId" | "startDate" | "endDate" | "careerLevelId" | "isActive" | "userId"> {
	careerLevelId: string | null,
	startDate: Date;
	endDate: Date | null;
}

const CreateAdditionalStaffingForUserForm: React.FC<CreateStaffingFormProps> = ({
	onSuccess,
	onCancel,
	orderData,
	staffedUserData,
	totalBudgetUnassigned,
	totalManDaysUnassigned,
	usersStaffings,
}) => {
	//const { t } = useTranslation();
	const [busy, setBusy] = useState(false);
	const [overlappingStaffingIds, setOverlappingStaffingIds] = useState<string[]>([]);
	const {
		data: allUsersData,
	} = useAllUsers();

	const schema = useMemo(() => {
		return yup.object({
			careerLevelId: yup.string().required(),
			dailyRateCents: yup.number().required(),
			endDate: yup
				.date()
				.transform(transformEmptyDateValueToNull)
				.nullable()
				.default(null)
				.min(yup.ref("startDate"), "Das Startdatum muss vor dem Enddatum liegen."),
			isBillable: yup.boolean().required(),
			manDays: yup.number().typeError(t("form:validationErrors.number.requiredTypeError")).required(),
			projectRoleName: yup.string().required(),
			startDate: yup.date().transform(transformEmptyDateValueToNull).nullable().required(),
		});
	}, []);

	const defaultValues = useMemo(() => {
		const startDate = findNextAvailableStartDateForStaffing(usersStaffings);

		return {
			careerLevelId: "" as CareerLevelId,
			dailyRateCents: 0,
			endDate: null,
			manDays: 0,
			isBillable: true,
			projectRoleName: "",
			startDate: startDate,

		};
	}, [orderData.startDate]);

	const {
		handleSubmit,
		control,
		formState: { errors, isValid, isDirty, isSubmitted },
		setValue,
		watch,
	} = useForm<FormData>({
		mode:"onChange",
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});
	const projectId = orderData.projectId;
	const careerLevelSelectOptions = useCareerLevelOptions();
	const orderId = orderData.id;
	const projectRoleSelectOptions = useProjectsProjectRolesOptions({ projectId, valueProperty: "displayName" });


	const careerLevelId = watch("careerLevelId");
	const endDate = watch("endDate");
	const startDate = watch("startDate");

	useEffect(() => {
		if (!allUsersData) return;
		const user = allUsersData.find(user => user.id === staffedUserData.id);
		if (user?.careerLevelId) {
			setValue("careerLevelId", user.careerLevelId);
		}
	}, [allUsersData, setValue, staffedUserData.id]);

	const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
		setBusy(true);
		try {

			const staffingData = {
				...data,
				careerLevelId: data.careerLevelId as CareerLevelId,
				projectId: projectId,
				projectPhaseId: orderData.projectPhaseId,
				orderId: orderId,
				startDate: formatDateToYYYYMMDD(data.startDate),
				userId: staffedUserData.id,
				endDate: data.endDate ? formatDateToYYYYMMDD(data.endDate) : null,
			};
			await createAdditionalStaffingForUser({ projectId, userId: staffedUserData.id, staffingData });
			onSuccess(staffedUserData.id);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		const overlappingStaffings = usersStaffings.filter((staffing) => {
			return doDateRangesWithOptionalEndDatesOverlap(staffing, { startDate, endDate });
		}).map((staffing) => staffing.id);
		setOverlappingStaffingIds(overlappingStaffings);
	}, [startDate, endDate, usersStaffings]);

	const formIsSubmittable = useFormIsSubmittable({
		enableInitially: true,
		isSubmitted,
		isValid,
		isLoading: busy,
		isDirty,
	});

	const careerLevelDisplayName = careerLevelId ? getCareerLevelDisplayNameById(careerLevelId as CareerLevelId) : EMPTY_VALUE_PLACEHOLDER;

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Zusätzliches Staffing für User erstellen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}

				<FormSectionGroup>
					<FormSection title="Projektmitarbeiter:in">
						<div className="flex items-center justify-between">
							<div className="flex items-center">
								<Avatar firstName={staffedUserData.firstName}
										lastName={staffedUserData.lastName}
										image={staffedUserData.avatarImage}
								/>
								<div className="ml-2 flex flex-col items-start">
									<div className="font-semibold">{staffedUserData.fullName}</div>
									<div className="-mt-0.5 text-sm">{careerLevelDisplayName}</div>
								</div>
							</div>
						</div>
					</FormSection>
					<FormSection title="Karrierestufe / Rolle">
						<div className="grid grid-cols-2 gap-x-6 gap-y-2">
							<Select name={"careerLevelId"}
									label="Karrierstufe (Staffing)"
									control={control}
									optionsData={careerLevelSelectOptions} />
							<ComboBox
								placeholder="Name der Rolle"
								label="Rolle im Projekt"
								name={"projectRoleName"}
								control={control}
								optionsData={projectRoleSelectOptions}
							/>
						</div>
					</FormSection>
					<StaffingBudgetFormSectionAllAttributes control={control}
															errors={errors}
															totalManDaysUnassigned={totalManDaysUnassigned}
															totalBudgetUnassigned={totalBudgetUnassigned}
															watch={watch} />

					<TimingFormSection<FormData> control={control}
												 autoUpdateMissingValues={false}
												 startDateIsClearable={false}
												 errors={errors}
												 title="Gültigkeit"
												 watch={watch}
												 setValue={setValue} />

					<UsersOtherStaffingsInOrderFormSection
						overlappingStaffingIds={overlappingStaffingIds}
						usersOtherStaffings={usersStaffings} />
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={!busy && isSubmitted && (!isValid || overlappingStaffingIds.length > 0)}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable || overlappingStaffingIds.length > 0}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default CreateAdditionalStaffingForUserForm;
